import { attributes, type HtmlValue, html, joinClasses } from 'BomdisoftComponents/html/index';
import type { ColorVariant } from 'BomdisoftComponents/types';
import { capitalizeFirstLetters } from '@bomdisoft/utilities/functions';

export type StatusIndicator = 'fill' | 'dot';

export type StatusSize = 'medium' | 'small';

export type StatusOptions = {
  action?: string;
  capitalize?: boolean;
  className?: string;
  icon?: HtmlValue;
  indicator?: StatusIndicator;
  isClickable?: boolean;
  outlined?: boolean;
  rounded?: boolean;
  selected?: boolean;
  size?: StatusSize;
  testId?: string;
  text: string;
  variant?: ColorVariant;
};

export const RoleNames = ['Test', 'User', 'Admin', 'Superuser'] as const;

export type RoleVariant = (typeof RoleNames)[number];

export type RoleChipOptions = {
  className?: string;
  outlined?: boolean;
  role?: RoleVariant;
};

const ROLE_VARIANTS: Readonly<Record<RoleVariant, ColorVariant>> = { Admin: 'warning', Superuser: 'error', Test: 'default', User: 'info' };

export const toRoleVariant = (role: RoleVariant): ColorVariant => ROLE_VARIANTS[role] ?? 'default';

export const renderStatus = ({ action, capitalize = true, className, icon, indicator = 'fill', isClickable, outlined, rounded, selected, size = 'medium', testId, text, variant = 'default' }: StatusOptions) => {
  const hasIcon = icon !== undefined && icon !== false && icon !== null;
  const isDot = !hasIcon && indicator === 'dot';
  const markers = attributes({
    'aria-pressed': isClickable ? String(selected ?? false) : undefined,
    class: joinClasses('bomdisoft-chip', className),
    'data-action': action,
    'data-clickable': isClickable,
    'data-indicator': hasIcon ? 'icon' : indicator === 'dot' ? 'dot' : undefined,
    'data-outlined': !isDot && outlined,
    'data-rounded': !isDot && rounded,
    'data-selected': selected,
    'data-size': size === 'small' ? 'small' : undefined,
    'data-testid': testId,
    'data-variant': variant,
  });
  const marker = hasIcon ? html`<span aria-hidden="true" class="bomdisoft-chip-icon">${icon}</span>` : indicator === 'dot' ? html`<span aria-hidden="true" class="bomdisoft-chip-dot"></span>` : '';
  const content = capitalize ? capitalizeFirstLetters(text) : text;
  return isClickable ? html`<button${markers} type="button">${marker}${content}</button>` : html`<span${markers}>${marker}${content}</span>`;
};

export const renderRoleChip = ({ className, outlined, role = 'User' }: RoleChipOptions = {}) => renderStatus({ capitalize: true, className, outlined, rounded: true, text: role, variant: toRoleVariant(role) });
